import type { ReactNode } from 'react';
import { Icon } from './Mark';
import { monogram } from '../lib/format';

export function Card({ children, className = '', tone }: { children: ReactNode; className?: string; tone?: 'plain' | 'red' | 'gold' | 'navy' | 'soft' }) {
  return <section className={`card ${tone ? `card-${tone}` : ''} ${className}`}>{children}</section>;
}

export function Progress({ value, max = 100, tone = 'red' }: { value: number; max?: number; tone?: 'red' | 'green' | 'gold' | 'white' }) {
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className={`progress progress-${tone}`} role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
      <div className="progress-fill" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Stat({ icon, value, label, sub, tone = 'red' }: { icon: Parameters<typeof Icon>[0]['name']; value: ReactNode; label: string; sub?: ReactNode; tone?: 'red' | 'green' | 'blue' | 'gold' | 'purple' | 'navy' }) {
  return (
    <div className="stat">
      <span className={`bubble bubble-${tone}`}>
        <Icon name={icon} />
      </span>
      <strong className="stat-value">{value}</strong>
      <span className="stat-label">{label}</span>
      {sub ? <span className="stat-sub">{sub}</span> : null}
    </div>
  );
}

export function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span className={`avatar avatar-${size}`} aria-hidden="true">
      {monogram(name)}
    </span>
  );
}

export function Chip({ on, onClick, children, tone = 'red' }: { on: boolean; onClick: () => void; children: ReactNode; tone?: 'red' | 'plain' }) {
  return (
    <button type="button" className={`chip ${on ? `chip-on chip-${tone}` : ''}`} aria-pressed={on} onClick={onClick}>
      {children}
    </button>
  );
}

export function Row({ icon, title, sub, right, tone = 'red', onClick, href }: { icon?: Parameters<typeof Icon>[0]['name']; title: ReactNode; sub?: ReactNode; right?: ReactNode; tone?: 'red' | 'green' | 'blue' | 'gold' | 'purple' | 'navy'; onClick?: () => void; href?: string }) {
  const body = (
    <>
      {icon ? (
        <span className={`bubble bubble-${tone} bubble-sm`}>
          <Icon name={icon} size={18} />
        </span>
      ) : null}
      <span className="row-main">
        <span className="row-title">{title}</span>
        {sub ? <span className="row-sub">{sub}</span> : null}
      </span>
      {right !== undefined ? <span className="row-right">{right}</span> : null}
    </>
  );
  if (href) {
    return (
      <a className="row row-link" href={href}>
        {body}
      </a>
    );
  }
  if (onClick) {
    return (
      <button type="button" className="row row-link" onClick={onClick}>
        {body}
      </button>
    );
  }
  return <div className="row">{body}</div>;
}

export function Toast({ text, onClose }: { text: string | null; onClose: () => void }) {
  if (!text) return null;
  return (
    <div className="toast" role="status">
      <span>{text}</span>
      <button type="button" className="icon-btn" onClick={onClose} aria-label="dismiss">
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children ? <p className="hint">{children}</p> : null}
    </div>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return <span className="eyebrow">{children}</span>;
}
